import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { adminApi, type CalendarBookingDto, type TrainerUnavailabilityDto } from '@/api/admin'
import { useTopBar } from '@/contexts/TopBarContext'
import { ChevronLeft, ChevronRight, Users, UserCheck, AlertTriangle, CalendarDays, CalendarOff, Copy, Check, RefreshCw, X } from 'lucide-react'
import { useAdminDates } from '@/hooks/useDates'

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
// Above this many active trainers, show a plain <select> instead of a chip per trainer.
const CHIP_TRAINER_LIMIT = 8

const AVATAR_PALETTE = [
  'bg-indigo-500', 'bg-emerald-500', 'bg-rose-500', 'bg-violet-500',
  'bg-orange-500', 'bg-cyan-600',   'bg-pink-500',  'bg-teal-500',
]

function trainerColourClass(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length]
}

function initials(name: string): string {
  const parts = name.trim().split(' ')
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '?'
  return ((parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '')).toUpperCase()
}

function TrainerAvatar({ name, size = 16 }: { name: string; size?: number }) {
  const bg = trainerColourClass(name)
  const sz = `${size}px`
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full text-white font-semibold shrink-0 ${bg}`}
      style={{ width: sz, height: sz, fontSize: size * 0.45 }}
      title={name}
    >
      {initials(name)}
    </span>
  )
}

function statusColour(status: CalendarBookingDto['status']) {
  switch (status) {
    case 'Booked':
    case 'Published':  return 'bg-indigo-100 text-indigo-800 border-indigo-200'
    case 'Completed':  return 'bg-green-100 text-green-800 border-green-200'
    default:           return 'bg-gray-100 text-gray-600 border-gray-200'
  }
}

function bookingWarnings(b: CalendarBookingDto): string[] {
  const warnings: string[] = []
  if (b.eventType === 'Booking' && b.attendeeCount > 0 && b.attendeeCount !== b.places)
    warnings.push(b.attendeeCount > b.places
      ? `${b.attendeeCount} attendees but only ${b.places} places booked`
      : `${b.places} places booked but only ${b.attendeeCount} attendees assigned`)
  if (!b.leadTrainerName && b.trainers.length > 0)
    warnings.push('No lead trainer assigned')
  return warnings
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month, 0).getDate()
}

// weekStartsOn: 0 = Sunday-first (US), 1 = Monday-first
function getStartDayOfWeek(year: number, month: number, weekStartsOn: 0 | 1) {
  const d = new Date(year, month - 1, 1).getDay()
  return (d - weekStartsOn + 7) % 7
}

function getWeekStart(weekStartsOn: 0 | 1): Date {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() - ((d.getDay() - weekStartsOn + 7) % 7))
  return d
}

function addDays(date: Date, n: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + n)
  return d
}

function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** True if dateStr (YYYY-MM-DD) falls within [u.startDate, u.endDate] inclusive. */
function covers(u: TrainerUnavailabilityDto, dateStr: string): boolean {
  return dateStr >= u.startDate && dateStr <= u.endDate
}

// Compact chip for month/week grid cells — course name only on mobile, full on sm+
function BookingChip({ b, navigate }: { b: CalendarBookingDto; navigate: (path: string) => void }) {
  const warnings = bookingWarnings(b)
  const hasWarning = warnings.length > 0
  const attendeeMismatch = b.eventType === 'Booking' && b.attendeeCount > 0 && b.attendeeCount !== b.places
  const chipClass = hasWarning ? 'bg-amber-50 text-amber-900 border-amber-300' : statusColour(b.status)
  const route = b.eventType === 'Session' ? `/admin/sessions/${b.id}` : `/admin/bookings/${b.id}`

  return (
    <button
      type="button"
      onClick={e => { e.stopPropagation(); navigate(route) }}
      title={hasWarning ? warnings.join('\n') : undefined}
      className={`w-full text-left px-1.5 sm:px-2 py-0.5 sm:py-1 rounded border text-xs leading-tight cursor-pointer hover:opacity-80 transition-opacity ${chipClass}`}
    >
      <div className="flex items-center gap-1">
        {hasWarning && <AlertTriangle size={9} className="shrink-0 text-amber-600" />}
        <span className="font-medium truncate">{b.courseName}</span>
        {b.eventType === 'Session' && <span className="shrink-0 text-[9px] opacity-50 font-normal">public</span>}
        {b.dayNumber != null && (
          <span className="shrink-0 text-[9px] opacity-60 font-normal">D{b.dayNumber}/{b.totalDays}</span>
        )}
      </div>
      {b.clientName && <div className="hidden sm:block text-[10px] opacity-75 truncate">{b.clientName}</div>}
      <div className="hidden sm:flex items-center justify-between mt-0.5">
        {b.leadTrainerName ? (
          <div className="flex items-center gap-1 text-[10px] opacity-80">
            <TrainerAvatar name={b.leadTrainerName} size={13} />
            <span className="truncate">{b.leadTrainerName}</span>
            {b.trainers.length > 1 && <span className="opacity-60">+{b.trainers.length - 1}</span>}
          </div>
        ) : (
          <div className="flex items-center gap-0.5 text-[10px] opacity-60">
            <UserCheck size={9} /><span>Trainer TBC</span>
          </div>
        )}
        <div
          className={`flex items-center gap-0.5 text-[10px] ${attendeeMismatch ? 'font-semibold' : 'opacity-60'}`}
          title={attendeeMismatch
            ? b.attendeeCount > b.places
              ? `${b.attendeeCount} attendees but only ${b.places} places booked`
              : `${b.places} places booked but only ${b.attendeeCount} attendees assigned`
            : `${b.places} places`}
        >
          {attendeeMismatch ? <AlertTriangle size={9} /> : <Users size={9} />}
          <span>{attendeeMismatch ? `${b.attendeeCount}/${b.places}` : b.places}</span>
        </div>
      </div>
    </button>
  )
}

// Full card for the day panel and mobile week list
function BookingCard({ b, navigate }: { b: CalendarBookingDto; navigate: (path: string) => void }) {
  const warnings = bookingWarnings(b)
  const hasWarning = warnings.length > 0
  const attendeeMismatch = b.eventType === 'Booking' && b.attendeeCount > 0 && b.attendeeCount !== b.places
  const cardClass = hasWarning ? 'border-amber-200 bg-amber-50' :
    (b.status === 'Booked' || b.status === 'Published') ? 'border-indigo-200 bg-indigo-50' :
    b.status === 'Completed' ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
  const route = b.eventType === 'Session' ? `/admin/sessions/${b.id}` : `/admin/bookings/${b.id}`

  return (
    <button
      type="button"
      onClick={() => navigate(route)}
      className={`w-full text-left px-3 py-2.5 rounded-lg border cursor-pointer hover:opacity-80 transition-opacity ${cardClass}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {hasWarning && <AlertTriangle size={12} className="shrink-0 text-amber-600" />}
            <span className="font-medium text-sm text-gray-900 truncate">{b.courseName}</span>
            {b.eventType === 'Session' && <span className="text-xs text-gray-400 font-normal shrink-0">Public</span>}
            {b.dayNumber != null && (
              <span className="shrink-0 text-xs text-gray-400 font-normal">Day {b.dayNumber}/{b.totalDays}</span>
            )}
          </div>
          {b.clientName && <p className="text-xs text-gray-500 truncate mt-0.5">{b.clientName}</p>}
          {b.leadTrainerName ? (
            <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-600">
              <TrainerAvatar name={b.leadTrainerName} size={14} />
              <span>{b.leadTrainerName}</span>
              {b.trainers.length > 1 && <span className="text-gray-400">+{b.trainers.length - 1}</span>}
            </div>
          ) : b.trainers.length > 0 ? (
            <div className="flex items-center gap-1 mt-1 text-xs text-amber-700">
              <UserCheck size={11} /><span>No lead trainer</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
              <UserCheck size={11} /><span>Trainer TBC</span>
            </div>
          )}
        </div>
        <div className={`flex items-center gap-0.5 text-xs shrink-0 mt-0.5 ${attendeeMismatch ? 'text-amber-700 font-semibold' : 'text-gray-500'}`}>
          {attendeeMismatch ? <AlertTriangle size={11} /> : <Users size={11} />}
          <span>{attendeeMismatch ? `${b.attendeeCount}/${b.places}` : b.places}</span>
        </div>
      </div>
    </button>
  )
}

export default function CalendarPage() {
  const { formatDate, weekStartsOn } = useAdminDates()
  const dayNames = weekStartsOn === 0 ? [DAY_NAMES[6], ...DAY_NAMES.slice(0, 6)] : DAY_NAMES
  const formatDayHeading = (dateStr: string): string => {
    return formatDate(dateStr + 'T12:00:00', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    })
  }

  useTopBar(<span className="text-sm font-medium text-gray-900">Calendar</span>)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const today = new Date()

  const [view, setView] = useState<'month' | 'week'>('month')
  const [year, setYear] = useState(today.getFullYear())
  const [month, setMonth] = useState(today.getMonth() + 1)
  const [weekStart, setWeekStart] = useState<Date>(() => getWeekStart(weekStartsOn))
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [showSubscribe, setShowSubscribe] = useState(false)
  const [copied, setCopied] = useState(false)
  // Holds the trainer's id (not name) so it can also drive the unavailability lookup below.
  const [trainerFilter, setTrainerFilter] = useState<string>('')

  useEffect(() => { setWeekStart(getWeekStart(weekStartsOn)) }, [weekStartsOn])

  // Only fetched once a single trainer is selected — showing every trainer's blocked days at
  // once on the shared tenant calendar would just read as unattributable grey noise.
  const { data: trainerUnavailability = [] } = useQuery({
    queryKey: ['trainer-unavailability', trainerFilter],
    queryFn: () => adminApi.getTrainerUnavailability(trainerFilter).then(r => r.data),
    enabled: !!trainerFilter,
  })
  const isTrainerUnavailable = (dateStr: string) =>
    !!trainerFilter && trainerUnavailability.some(u => covers(u, dateStr))

  const weekEnd  = useMemo(() => addDays(weekStart, 6), [weekStart])
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart])

  const wsYear = weekStart.getFullYear(), wsMonth = weekStart.getMonth() + 1
  const weYear = weekEnd.getFullYear(),   weMonth = weekEnd.getMonth() + 1
  const weekSpansMonths = wsYear !== weYear || wsMonth !== weMonth

  const { data: monthBookings = [], isLoading: monthLoading } = useQuery({
    queryKey: ['calendar-bookings', year, month],
    queryFn: () => adminApi.getCalendarBookings(year, month).then(r => r.data),
    enabled: view === 'month',
  })

  const { data: weekBookings1 = [], isLoading: weekLoading } = useQuery({
    queryKey: ['calendar-bookings', wsYear, wsMonth],
    queryFn: () => adminApi.getCalendarBookings(wsYear, wsMonth).then(r => r.data),
    enabled: view === 'week',
  })

  const { data: weekBookings2 = [] } = useQuery({
    queryKey: ['calendar-bookings', weYear, weMonth],
    queryFn: () => adminApi.getCalendarBookings(weYear, weMonth).then(r => r.data),
    enabled: view === 'week' && weekSpansMonths,
  })

  const isLoading = view === 'month' ? monthLoading : weekLoading

  const bookings = useMemo(() => {
    if (view === 'month') return monthBookings
    return weekSpansMonths ? [...weekBookings1, ...weekBookings2] : weekBookings1
  }, [view, monthBookings, weekBookings1, weekBookings2, weekSpansMonths])

  const { data: tokenData } = useQuery({
    queryKey: ['calendar-feed-token'],
    queryFn: () => adminApi.getCalendarFeedToken().then(r => r.data),
    enabled: showSubscribe,
  })

  const generateMutation = useMutation({
    mutationFn: () => adminApi.generateCalendarFeedToken(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['calendar-feed-token'] }),
  })

  const apiBase = import.meta.env.VITE_API_URL ?? window.location.origin
  const feedUrl = tokenData?.token ? `${apiBase}/api/v1/calendar/feed?token=${tokenData.token}` : null

  const copyUrl = () => {
    if (!feedUrl) return
    navigator.clipboard.writeText(feedUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const prevMonth = () => { setSelectedDate(null); if (month === 1) { setYear(y => y - 1); setMonth(12) } else setMonth(m => m - 1) }
  const nextMonth = () => { setSelectedDate(null); if (month === 12) { setYear(y => y + 1); setMonth(1) } else setMonth(m => m + 1) }
  const prevWeek  = () => { setSelectedDate(null); setWeekStart(w => addDays(w, -7)) }
  const nextWeek  = () => { setSelectedDate(null); setWeekStart(w => addDays(w, 7)) }

  const goToday = () => {
    setSelectedDate(null)
    if (view === 'month') { setYear(today.getFullYear()); setMonth(today.getMonth() + 1) }
    else setWeekStart(getWeekStart(weekStartsOn))
  }

  const switchView = (v: 'month' | 'week') => {
    setView(v)
    setSelectedDate(null)
    if (v === 'week') {
      const monthHasToday = today.getFullYear() === year && today.getMonth() + 1 === month
      if (monthHasToday) {
        setWeekStart(getWeekStart(weekStartsOn))
      } else {
        const firstOfMonth = new Date(year, month - 1, 1)
        const dow = firstOfMonth.getDay()
        setWeekStart(addDays(firstOfMonth, -((dow - weekStartsOn + 7) % 7)))
      }
    } else {
      setYear(weekStart.getFullYear())
      setMonth(weekStart.getMonth() + 1)
    }
  }

  const toggleDate = (dateStr: string) => setSelectedDate(d => d === dateStr ? null : dateStr)

  // All active trainers, not just ones with a booking in the currently-viewed month/week — the
  // whole point of filtering is often to check availability for someone who has nothing booked
  // yet, so deriving this from `bookings` would hide exactly the trainer you're looking for.
  const { data: allTrainers = [] } = useQuery({
    queryKey: ['trainers'],
    queryFn: () => adminApi.getTrainers().then(r => r.data),
  })
  const trainerOptions = useMemo(() =>
    allTrainers
      .filter(t => t.isActive)
      .map(t => ({ id: t.id, name: `${t.firstName} ${t.lastName}` }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [allTrainers])

  const filteredBookings = useMemo(() =>
    trainerFilter
      ? bookings.filter(b => b.trainers.some(t => t.trainerId === trainerFilter))
      : bookings,
    [bookings, trainerFilter])

  const daysInMonth = getDaysInMonth(year, month)
  const startDow   = getStartDayOfWeek(year, month, weekStartsOn)
  const totalCells = Math.ceil((startDow + daysInMonth) / 7) * 7

  const byDay = useMemo(() => {
    const r: Record<number, CalendarBookingDto[]> = {}
    if (view !== 'month') return r
    for (const b of filteredBookings) {
      const d = parseInt(b.bookingDate.split('-')[2])
      ;(r[d] ??= []).push(b)
    }
    return r
  }, [filteredBookings, view])

  const byDateStr = useMemo(() => {
    const r: Record<string, CalendarBookingDto[]> = {}
    for (const b of filteredBookings) (r[b.bookingDate] ??= []).push(b)
    return r
  }, [filteredBookings])

  const selectedDateBookings = selectedDate ? (byDateStr[selectedDate] ?? []) : []

  const todayStr = toDateStr(today)

  const headerLabel = useMemo(() => {
    if (view === 'month') return `${MONTH_NAMES[month - 1]} ${year}`
    const s = weekStart, e = weekEnd
    const sm = MONTH_NAMES[s.getMonth()].slice(0, 3)
    const em = MONTH_NAMES[e.getMonth()].slice(0, 3)
    if (s.getFullYear() !== e.getFullYear())
      return `${s.getDate()} ${sm} ${s.getFullYear()} – ${e.getDate()} ${em} ${e.getFullYear()}`
    if (s.getMonth() !== e.getMonth())
      return `${s.getDate()} ${sm} – ${e.getDate()} ${em} ${s.getFullYear()}`
    return `${s.getDate()}–${e.getDate()} ${sm} ${s.getFullYear()}`
  }, [view, year, month, weekStart, weekEnd])

  return (
    <div>
      {/* Header — stacks on mobile, inline on sm+ */}
      <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-1.5">
          <button onClick={view === 'month' ? prevMonth : prevWeek} className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 cursor-pointer transition-colors">
            <ChevronLeft size={18} />
          </button>
          <span className="text-sm font-semibold text-gray-800 text-center min-w-[152px]">{headerLabel}</span>
          <button onClick={view === 'month' ? nextMonth : nextWeek} className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 cursor-pointer transition-colors">
            <ChevronRight size={18} />
          </button>
          <button onClick={goToday} className="ml-1 px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors">
            Today
          </button>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
            <button
              onClick={() => switchView('month')}
              className={`px-3 py-1.5 cursor-pointer transition-colors ${view === 'month' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              Month
            </button>
            <button
              onClick={() => switchView('week')}
              className={`px-3 py-1.5 border-l border-gray-200 cursor-pointer transition-colors ${view === 'week' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              Week
            </button>
          </div>

          <button
            onClick={() => setShowSubscribe(s => !s)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors"
          >
            <CalendarDays size={15} />
            <span className="hidden sm:inline">Subscribe</span>
          </button>
        </div>
      </div>

      {/* Subscribe panel */}
      {showSubscribe && (
        <div className="mb-6 bg-indigo-50 border border-indigo-100 rounded-xl p-4 space-y-3">
          <div>
            <p className="text-sm font-semibold text-indigo-900">Subscribe in Outlook (or any calendar app)</p>
            <p className="text-xs text-indigo-600 mt-0.5">
              Copy the link below and paste it into Outlook → Add calendar → Subscribe from web.
              Your calendar will stay in sync automatically.
            </p>
          </div>
          {!tokenData?.token && !generateMutation.isPending && (
            <button
              onClick={() => generateMutation.mutate()}
              className="px-3 py-1.5 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 cursor-pointer transition-colors"
            >
              Generate subscription link
            </button>
          )}
          {generateMutation.isPending && <p className="text-xs text-indigo-500">Generating…</p>}
          {feedUrl && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  readOnly value={feedUrl}
                  className="flex-1 min-w-0 text-xs font-mono bg-white border border-indigo-200 rounded-lg px-3 py-2 text-gray-700 select-all"
                  onFocus={e => e.target.select()}
                />
                <button
                  onClick={copyUrl}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg border border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-100 cursor-pointer transition-colors whitespace-nowrap shrink-0"
                >
                  {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
                </button>
              </div>
              <div className="flex items-center justify-between gap-4">
                <p className="text-xs text-indigo-500">
                  Treat this link like a password — anyone with it can view your bookings.
                </p>
                <button
                  onClick={() => generateMutation.mutate()}
                  disabled={generateMutation.isPending}
                  className="flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700 cursor-pointer transition-colors shrink-0"
                  title="Generate a new link (invalidates the old one)"
                >
                  <RefreshCw size={12} /> Regenerate
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Legend + trainer filter — chips for a small roster, a plain dropdown once it's too
          many to show inline (now that this lists every active trainer, not just ones with a
          booking in view, a tenant with a large team would otherwise get a wall of chips). */}
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          {(['Booked', 'Completed'] as const).map(s => (
            <span key={s} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs border ${statusColour(s)}`}>
              {s}
            </span>
          ))}
          {trainerOptions.length > 0 && trainerOptions.length <= CHIP_TRAINER_LIMIT && (
            <>
              <span className="text-xs text-gray-300">|</span>
              {trainerOptions.map(t => (
                <button
                  key={t.id}
                  onClick={() => setTrainerFilter(f => f === t.id ? '' : t.id)}
                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs border transition-colors cursor-pointer ${
                    trainerFilter === t.id
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <TrainerAvatar name={t.name} size={14} />
                  {t.name}
                  {trainerFilter === t.id && <X size={11} className="ml-0.5 opacity-70" />}
                </button>
              ))}
            </>
          )}
        </div>
        {trainerOptions.length > CHIP_TRAINER_LIMIT && (
          <select
            value={trainerFilter}
            onChange={e => setTrainerFilter(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white cursor-pointer"
          >
            <option value="">All trainers</option>
            {trainerOptions.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
      </div>

      {trainerFilter && trainerUnavailability.length > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-3">
          <CalendarOff size={12} />
          Greyed days are when this trainer is marked unavailable.
        </div>
      )}

      {/* ── MONTH VIEW ── */}
      {view === 'month' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <div className="grid grid-cols-7 border-b border-gray-100">
            {dayNames.map(d => (
              <div key={d} className="py-2.5 text-center text-xs font-semibold text-gray-400 uppercase tracking-wide">
                {d}
              </div>
            ))}
          </div>
          {isLoading ? (
            <div className="py-24 text-center text-gray-400 text-sm">Loading…</div>
          ) : (
            <div className="grid grid-cols-7">
              {Array.from({ length: totalCells }).map((_, idx) => {
                const day = idx - startDow + 1
                const isCurrentMonth = day >= 1 && day <= daysInMonth
                const dateStr = isCurrentMonth
                  ? `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
                  : ''
                const isToday = dateStr === todayStr
                const isSelected = dateStr !== '' && dateStr === selectedDate
                const dayBookings = isCurrentMonth ? (byDay[day] ?? []) : []
                const isWeekend = (idx % 7) >= 5
                const isUnavailable = isCurrentMonth && isTrainerUnavailable(dateStr)

                return (
                  <div
                    key={idx}
                    className={`min-h-[80px] sm:min-h-[120px] border-b border-r border-gray-100 p-1 sm:p-2 last:border-r-0 ${
                      isSelected ? 'bg-indigo-50/60' :
                      !isCurrentMonth ? 'bg-gray-50/50' :
                      isUnavailable ? 'bg-gray-100' :
                      isWeekend ? 'bg-slate-50/60' : 'bg-white'
                    }`}
                  >
                    {isCurrentMonth && (
                      <>
                        <div
                          onClick={() => toggleDate(dateStr)}
                          className={`text-xs font-semibold mb-1 w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full cursor-pointer select-none ${
                            isToday ? 'bg-indigo-600 text-white' :
                            isSelected ? 'bg-indigo-200 text-indigo-900' :
                            'text-gray-500 hover:bg-gray-100'
                          }`}
                        >
                          {day}
                        </div>
                        {isUnavailable && (
                          <div className="flex items-center gap-1 text-[9px] text-gray-400 mb-0.5">
                            <CalendarOff size={9} /><span className="hidden sm:inline">Unavailable</span>
                          </div>
                        )}
                        <div className="space-y-0.5">
                          {dayBookings.map(b => <BookingChip key={b.dayNumber != null ? `${b.id}-${b.dayNumber}` : b.id} b={b} navigate={navigate} />)}
                        </div>
                      </>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── WEEK VIEW ── */}
      {view === 'week' && (
        <>
          {/* Mobile: stacked day list */}
          <div className="sm:hidden space-y-5">
            {isLoading && <p className="text-center text-gray-400 py-8">Loading…</p>}
            {!isLoading && weekDays.map((day, i) => {
              const dateStr = toDateStr(day)
              const dayBookings = byDateStr[dateStr] ?? []
              const isToday = dateStr === todayStr
              const isUnavailable = isTrainerUnavailable(dateStr)
              return (
                <div key={dateStr}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{dayNames[i]}</span>
                    <span className={`w-6 h-6 flex items-center justify-center rounded-full text-sm font-bold ${
                      isToday ? 'bg-indigo-600 text-white' : 'text-gray-800'
                    }`}>
                      {day.getDate()}
                    </span>
                    {dayBookings.length > 0 && (
                      <span className="text-xs text-gray-400">{dayBookings.length} booking{dayBookings.length !== 1 ? 's' : ''}</span>
                    )}
                    {isUnavailable && (
                      <span className="flex items-center gap-1 text-xs text-gray-400"><CalendarOff size={11} />Unavailable</span>
                    )}
                  </div>
                  {dayBookings.length === 0
                    ? <p className="text-xs text-gray-300">No bookings</p>
                    : <div className="space-y-1.5">{dayBookings.map(b => <BookingCard key={b.dayNumber != null ? `${b.id}-${b.dayNumber}` : b.id} b={b} navigate={navigate} />)}</div>
                  }
                </div>
              )
            })}
          </div>

          {/* Desktop: 7-column grid */}
          <div className="hidden sm:block bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="grid grid-cols-7 border-b border-gray-100">
              {weekDays.map((day, i) => {
                const dateStr = toDateStr(day)
                const isToday = dateStr === todayStr
                const isSelected = dateStr === selectedDate
                return (
                  <div
                    key={i}
                    onClick={() => toggleDate(dateStr)}
                    className={`py-2.5 text-center border-r border-gray-100 last:border-r-0 cursor-pointer transition-colors ${
                      isSelected ? 'bg-indigo-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className={`text-xs font-semibold uppercase tracking-wide ${isToday ? 'text-indigo-600' : 'text-gray-400'}`}>
                      {dayNames[i]}
                    </div>
                    <div className={`mx-auto mt-1 w-7 h-7 flex items-center justify-center rounded-full text-sm font-bold ${
                      isToday ? 'bg-indigo-600 text-white' :
                      isSelected ? 'bg-indigo-200 text-indigo-900' :
                      'text-gray-700'
                    }`}>
                      {day.getDate()}
                    </div>
                  </div>
                )
              })}
            </div>
            {isLoading ? (
              <div className="py-24 text-center text-gray-400 text-sm">Loading…</div>
            ) : (
              <div className="grid grid-cols-7">
                {weekDays.map((day, i) => {
                  const dateStr = toDateStr(day)
                  const isToday = dateStr === todayStr
                  const isSelected = dateStr === selectedDate
                  const dayBookings = byDateStr[dateStr] ?? []
                  const isWeekend = day.getDay() === 0 || day.getDay() === 6
                  const isUnavailable = isTrainerUnavailable(dateStr)
                  return (
                    <div
                      key={i}
                      className={`min-h-[150px] border-r border-gray-100 p-2 last:border-r-0 ${
                        isSelected ? 'bg-indigo-50/60' :
                        isUnavailable ? 'bg-gray-100' :
                        isToday ? 'bg-indigo-50/30' :
                        isWeekend ? 'bg-slate-50/60' : 'bg-white'
                      }`}
                    >
                      {isUnavailable && (
                        <div className="flex items-center gap-1 text-[10px] text-gray-400 mb-1">
                          <CalendarOff size={10} />Unavailable
                        </div>
                      )}
                      <div className="space-y-1">
                        {dayBookings.map(b => <BookingChip key={b.dayNumber != null ? `${b.id}-${b.dayNumber}` : b.id} b={b} navigate={navigate} />)}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* ── DAY PANEL (modal) — an in-flow panel below a tall month grid could land below the
          fold; a modal is visible no matter where in the grid you clicked. ── */}
      {selectedDate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setSelectedDate(null)}>
        <div className="bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden w-full max-w-md max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
            <h3 className="font-semibold text-gray-900 text-sm">{formatDayHeading(selectedDate)}</h3>
            <button
              onClick={() => setSelectedDate(null)}
              className="text-gray-400 hover:text-gray-600 cursor-pointer p-0.5 rounded transition-colors"
            >
              <X size={16} />
            </button>
          </div>
          <div className="p-4 overflow-y-auto">
            {selectedDateBookings.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No bookings on this day.</p>
            ) : (
              <div className="space-y-2">
                {selectedDateBookings.map(b => <BookingCard key={b.dayNumber != null ? `${b.id}-${b.dayNumber}` : b.id} b={b} navigate={navigate} />)}
              </div>
            )}
          </div>
        </div>
        </div>
      )}
    </div>
  )
}
